import { of as observableOf, forkJoin } from 'rxjs'
import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core'
import { CarCodes, GroupCodes } from '@exchange/domain/lookups'
import {
  ClaimLineServiceAdjustmentODataService,
  IClaimLineServiceAdjustment,
  IClaimLineView
} from '@exchange/domain/.'
import { GridSize, Option } from 'common-components'
import { ToastrService } from 'ngx-toastr'
import { map } from 'rxjs/operators'

interface IModel {
  claimAdjustmentReasons: IClaimLineServiceAdjustment[]
}

@Component({
  selector: 'app-response-codes-claim-line-adjustment-reasons',
  templateUrl: './response-codes-claim-line-adjustment-reasons.component.html',
  styleUrls: ['./response-codes-claim-line-adjustment-reasons.component.scss'],
  standalone: false
})
export class ResponseCodesClaimLineAdjustmentReasonsComponent implements OnInit {
  @Input() claimLineView: IClaimLineView
  @Output() alertMessage = new EventEmitter<any>()

  GridSize = GridSize
  CarCodes = CarCodes.Codes
  carCodeOptions: Option[] = []

  groupCodes = GroupCodes
  groupCodeOptions: Option[] = []
  model: IModel = {
    claimAdjustmentReasons: []
  }

  constructor(
    private odata: ClaimLineServiceAdjustmentODataService,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.loadGroupCodes()
    this.loadClaimAdjustmentReasonsFromDatabase()
  }

  loadClaimAdjustmentReasonsFromDatabase() {
    this.odata
      .query()
      .filter(
        `ClaimId eq ${this.claimLineView.ClaimId} and ServiceId eq ${this.claimLineView.ServiceId}`
      )
      .exec()
      .subscribe(response => {
        const records = response.data

        while (records.length < 5) {
          records.push({
            ClaimId: this.claimLineView.ClaimId,
            ServiceId: this.claimLineView.ServiceId
          } as IClaimLineServiceAdjustment)
        }

        this.model = {
          claimAdjustmentReasons: records
        }

        this.loadCarCodes()
      })
  }

  filteredCarCodeOptions(item: IClaimLineServiceAdjustment) {
    // We only want to show codes that THIS select can choose right now.
    // That means the code isn't being used anywhere but by THIS control
    // (show that one in the list so ng-select can select it).
    return this.carCodeOptions.filter(x => {
      return x.id === item.Code || !this.model.claimAdjustmentReasons.find(y => y.Code === x.id)
    })
  }

  loadGroupCodes() {
    this.groupCodeOptions = this.groupCodes.map(
      x => new Option(x.groupCode, `${x.groupCode} - ${x.name}`, x)
    )
  }

  loadCarCodes() {
    this.carCodeOptions = this.CarCodes.map(
      x => new Option(x.code, `${x.code} - ${x.description}`, x)
    )
  }

  saveClaimAdjustmentReasons() {
    const setId = item => resp => {
      if (item) {
        item.Id = (resp || {}).Id
      }
    }

    const observables = this.model.claimAdjustmentReasons.map(item => {
      let obs = null

      if (item.Id && !item.GroupCode) {
        obs = this.odata.delete(item.Id).exec()
      } else if (item.Id && item.GroupCode) {
        obs = this.odata.put(item, item.Id).exec()
        return obs
      } else if (!item.Id && item.GroupCode) {
        obs = this.odata.post(item).exec()
      } else {
        obs = observableOf(null)
      }

      return obs.pipe(map(setId(item)))
    })

    forkJoin(observables).subscribe(() =>
      this.toastr.success('Claim Adjustment Reasons saved successfully.')
    )
  }

  getActiveGroupCode(item: IClaimLineServiceAdjustment) {
    if (item.GroupCode) {
      return this.groupCodeOptions.filter(x => x.id === item.GroupCode)
    }
    return null
  }

  selectGroupCode(item: IClaimLineServiceAdjustment) {
    return (event: any) => {
      item.GroupCode = event.id
    }
  }

  removeGroupCode(item: IClaimLineServiceAdjustment) {
    return (event: any) => {
      item.GroupCode = undefined
    }
  }

  getActiveCarCode(item: IClaimLineServiceAdjustment) {
    if (item.Code) {
      return this.carCodeOptions.filter(x => x.id === item.Code)
    }

    return null
  }

  selectCarCode(item: IClaimLineServiceAdjustment) {
    return (event: any) => {
      item.Code = event.id
      this.loadCarCodes()
    }
  }

  removeCarCode(item: IClaimLineServiceAdjustment) {
    return (event: any) => {
      item.Code = undefined
      this.loadCarCodes()
    }
  }

  resetCarForm() {
    this.loadClaimAdjustmentReasonsFromDatabase()
  }

  validateClaimAdjustment() {
    const hasValue = val => val !== undefined && val !== null
    const validationErrors = []

    this.model.claimAdjustmentReasons.forEach((x, idx) => {
      // If we have any data, then we MUST have groupCode, code, and amount

      if (hasValue(x.GroupCode) || hasValue(x.Code) || hasValue(x.Quantity) || hasValue(x.Amount)) {
        if (!hasValue(x.GroupCode)) {
          validationErrors.push(`Line ${idx + 1}: Group Code is required.`)
        }

        if (!hasValue(x.Code)) {
          validationErrors.push(`Line ${idx + 1}: Code is required.`)
        }

        if (!hasValue(x.Amount)) {
          validationErrors.push(`Line ${idx + 1}: Amount is required.`)
        }
      }
    })

    return validationErrors
  }

  carCodesIsValid() {
    return this.validateClaimAdjustment().length === 0
  }

  canEditClaimAdjustments() {
    const notAllowed = ['Pending Remit', '837 Out']
    return notAllowed.indexOf(this.claimLineView.Status) === -1
  }
}
